import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'react-qr-code';
import { paymentAPI, customerAPI, configAPI, broadbandAPI, catvAPI, getPublicReceiptUrl } from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { CreditCard, Printer, Gift, Upload, Check, ChevronDown } from 'lucide-react';
import { buildUpiLink, upiEntriesFor } from '../../utils/upi';
import { startOnlinePayment } from '../../utils/onlinePayment';
import { dtdStartsTomorrowForExpiry } from '../../utils/dtdCutoff';
import DtdAfter8pmNotice from '../../components/common/DtdAfter8pmNotice';
import StbOnNotice from '../../components/common/StbOnNotice';
import { stbOnNoticeApplies } from '../../utils/stbOnNotice';
import SearchableSelect from '../../components/common/SearchableSelect';
import { paidByLabel } from '../../utils/paidByLabel';
import { useCompanyIdentity } from '../../hooks/useCompanyIdentity';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtDate = d => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

// Broadband always pays its own assigned plan price, 1 month at a time — no bundle here.
const BB_OFFER = { months: 1, payMonths: 1 };

// CATV "DTD" (day-to-day / prepaid) duration options. The 6 & 12 month tiers carry a fixed
// pay-5-get-6 / pay-10-get-12 discount, but ONLY when renewing within 10 days of the current
// expiry — otherwise they fall back to full linear pricing. 3-month has no discount either way.
// 1 & 2 months are only offered to a customer with billing.dtdAllowShortMonths set (same
// per-customer toggle as CustomerRenewPage.js's CATV_MONTHS); STB-free-to-use stays 3/6/12.
const CATV_DTD_TIERS = [
  { months: 1,  label: '1 Month' },
  { months: 2,  label: '2 Months' },
  { months: 3,  label: '3 Months' },
  { months: 6,  label: '6 Months',  discountPayMonths: 5,  badge: 'Pay 5, Get 1 Free!' },
  { months: 12, label: '12 Months', discountPayMonths: 10, badge: 'Pay 10, Get 2 Free!' },
];
const DISCOUNT_WINDOW_DAYS = 10;

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const startOfDay = d => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  return Math.round((startOfDay(dateStr) - startOfDay(new Date())) / 86400000);
}

export default function CustomerPayments() {
  const { name: companyName } = useCompanyIdentity('catv');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [customer, setCustomer] = useState(null);
  const [config, setConfig]     = useState({ upiEntries: [], upiName: 'AngelNet', catvUpiEntries: [], catvUpiName: 'AngelNet' });
  const [dtdMonths, setDtdMonths] = useState(3);
  // Broadband-only "Show QR Code" flow (see 2026-09-05 — removed for CATV, kept for
  // Broadband).
  const [showQR, setShowQR]     = useState(false);
  const [selUpi, setSelUpi]     = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);
  // When the connection has no plan/package assigned yet, let the customer pick one from
  // what's on offer so there's still a price to pay for.
  const [bbPlans, setBbPlans] = useState([]);
  const [catvPackages, setCatvPackages] = useState([]);
  const [pickedPlan, setPickedPlan] = useState(null);
  const [payingOnlinePg, setPayingOnlinePg] = useState(false);
  const [show8pmNotice, setShow8pmNotice] = useState(false);
  const [showStbNotice, setShowStbNotice] = useState(false);
  // Broadband self-service add-ons (2026-09-05) — see backend/src/utils/addons.js.
  const [selectedAddonIds, setSelectedAddonIds] = useState(new Set());

  useEffect(() => {
    Promise.all([
      paymentAPI.getAll({ limit: 20 }),
      customerAPI.getMe(),
      configAPI.get(),
      broadbandAPI.getPlans({ activeOnly: 'true' }),
      catvAPI.getPackages({ activeOnly: 'true' }),
    ]).then(([pRes, cRes, cfgRes, bbRes, catvRes]) => {
      setPayments(pRes.data.data || []);
      setCustomer(cRes.data.data);
      if (cfgRes.data?.data) setConfig(cfgRes.data.data);
      setBbPlans(bbRes.data?.data || []);
      setCatvPackages(catvRes.data?.data || []);
    }).catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));

    // Landing back here after AblePay's hosted-redirect flow (see utils/onlinePayment.js /
    // routes/webhooks.js /ablepay/return) — Razorpay never navigates away, so it never
    // hits this branch.
    const params = new URLSearchParams(window.location.search);
    const paymentResult = params.get('payment');
    if (paymentResult === 'success') toast.success('Payment successful!');
    else if (paymentResult === 'failed') toast.error(params.get('message') || 'Payment failed — please try again.');
  }, []); // eslint-disable-line

  // A CATV connection and a Broadband connection are sometimes registered as two separate
  // accounts sharing one login (linked via the admin's "Link Account" panel) — combine them
  // here so a combo customer can pay either service. Mirrors CustomerDashboard.js.
  const linked     = customer?.linkedAccount || null;
  const bbSource   = customer?.customerType !== 'catv' ? customer : linked;
  const catvSource = customer?.customerType !== 'broadband' ? customer : linked;
  const hasBB      = (bbSource?.customerType === 'broadband' || bbSource?.customerType === 'both') && !!bbSource?.broadband;
  const hasCatv    = (catvSource?.customerType === 'catv' || catvSource?.customerType === 'both') && !!catvSource?.catv;

  // CATV customers split into two billing categories (Customer.billing.durationType, kept in
  // sync with the legacy catv.catvType field — see CustomerDetailPage.js's setDurationType):
  //  - 'regular' — billed by the monthly cron, portal only shows outstanding balance to clear.
  //  - 'dtd'     — prepaid, self-service: browse visible packages, pick a 3/6/12-month term.
  const catvDurationType = catvSource?.billing?.durationType || 'regular';

  // Combo (CableTV + Broadband) customers: which service actually carries a real,
  // independently-payable due decides the default tab — mirrors the backend's routing rule
  // (utils/paymentLink.js gatewayEnabledForCustomer / renewalOrderService.createRenewalOrder,
  // fixed 2026-08-03, extended 2026-09-02). This page was left on the original 2026-07-27
  // "combo always shows Broadband" rule and never got updated to match, so a linked CATV
  // side billing independently (durationType 'regular'/'amp' — its own monthly cron-
  // generated bill, e.g. SURBHI BAG - 9) landed the customer on the Broadband tab with no
  // way to actually pay the CableTV bill they owed. Broadband is still forced when: a
  // single-doc 'both' account's balance is genuinely ambiguous (not the free ₹0 auto-renew
  // bundle, where CATV alone bills), or a linked CATV side's balance is itself generated
  // FROM the broadband payment (durationType 'dtd' — the old combo-split, no independent due).
  const isBothDoc = customer?.customerType === 'both';
  const bothCatvBillsOnly = isBothDoc && bbSource?.broadband?.autoRenew === true && catvDurationType !== 'dtd';
  const forcedBroadband = (isBothDoc && !bothCatvBillsOnly) || (!isBothDoc && catvDurationType === 'dtd');
  const isCombo     = hasBB && hasCatv;
  const service     = isCombo ? (forcedBroadband ? 'broadband' : 'catv') : (hasCatv ? 'catv' : 'broadband');
  const isCatvPay   = service === 'catv';
  const isCatvDtd        = isCatvPay && catvDurationType === 'dtd';
  const isCatvRegular    = isCatvPay && !isCatvDtd;
  const catvOutstanding  = catvSource?.outstandingBalance || 0;

  // Broadband dtd (day-to-day, prepaid) accounts can also pay via Online PG (gateway
  // checkout) — only offered once a real plan is assigned, since Online PG's create-order
  // resolves the plan/price server-side from the account itself.
  const bbDurationType = bbSource?.billing?.durationType || 'regular';
  const isBbDtd = !isCatvPay && bbDurationType === 'dtd';
  const isBbRegular = !isCatvPay && !isBbDtd;
  const bbOutstanding = bbSource?.outstandingBalance || 0;

  // Discount window: the 6/12-month DTD tiers only get their pay-5/pay-10 pricing within
  // 10 days of the current CATV expiry — otherwise full linear price applies.
  const catvExpiryDays      = daysUntil(catvSource?.catv?.expiryDate);
  const catvDiscountEligible = catvExpiryDays === null || catvExpiryDays <= DISCOUNT_WINDOW_DAYS;

  // DTD package the customer is paying for — defaults to whatever's currently assigned
  // (matched against the visible catalog) but can be switched to any other visible package.
  const dtdPackage = isCatvDtd
    ? (pickedPlan || catvPackages.find(p => p._id === catvSource?.catv?.package?._id) || null)
    : null;
  const dtdShortMonthsAllowed = !catvSource?.catv?.stbFreeToUse && !!catvSource?.billing?.dtdAllowShortMonths;
  const dtdTiers = CATV_DTD_TIERS.filter(t => t.months >= 3 || dtdShortMonthsAllowed);
  const dtdTier = dtdTiers.find(t => t.months === dtdMonths) || dtdTiers[0];
  const dtdDiscountApplies = !!(dtdTier.discountPayMonths && catvDiscountEligible);
  const dtdPayMonths = dtdDiscountApplies ? dtdTier.discountPayMonths : dtdTier.months;

  const assignedBbPrice = bbSource?.broadband?.plan?.price || 0;
  // No plan assigned yet — fall back to whatever the customer picked below.
  const bbPrice = assignedBbPrice > 0 ? assignedBbPrice : (pickedPlan?.price || 0);
  // No plan assigned yet + nothing owed, but the customer picked one from the search
  // dropdown below — Online PG can charge for that pick (see handlePayOnlinePg).
  const isBbUnassignedPicked = !isCatvPay && assignedBbPrice === 0 && (bbSource?.outstandingBalance || 0) <= 0 && !!pickedPlan;

  // Self-service add-ons — broadband only, and only once a real plan is assigned (buying an
  // add-on alongside picking a brand-new plan for the first time isn't offered, to avoid
  // that combinatorial case). Not a recurring charge — additionalPlans isn't touched by any
  // billing cron, this is a one-off purchase attached to the account on successful payment.
  const bbAddons = bbPlans.filter(p => p.type === 'addon');
  const showAddons = !isCatvPay && assignedBbPrice > 0 && bbAddons.length > 0;
  const addonsTotal = showAddons
    ? bbAddons.filter(a => selectedAddonIds.has(a._id)).reduce((s, a) => s + (a.price || 0), 0)
    : 0;
  const toggleAddon = (id) => setSelectedAddonIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const upiAmount = isCatvRegular
    ? catvOutstanding
    : isCatvDtd
      ? (dtdPackage?.price || 0) * dtdPayMonths
      : bbPrice > 0 ? bbPrice * BB_OFFER.payMonths + addonsTotal : 0;
  // Online PG's regular-billing cases pay outstandingBalance (server-resolved), NOT the
  // plan price upiAmount computes — this is what's actually shown on the button and what
  // create-order will charge, so they can't drift. dtd cases (both services) do use
  // upiAmount, since /me/catv-dtd/create-order mirrors this exact discount calculation
  // server-side.
  const onlinePgAmount = isCatvRegular
    ? catvOutstanding
    : isBbUnassignedPicked
      ? pickedPlan.price
      : (isBbDtd || isCatvDtd) ? upiAmount : bbOutstanding;

  // Settings → Payments picks gateway OR QR per service (2026-10-22); only that one shows.
  // CableTV QR uses the customer's entity's own UPI entries (blank-entity ones otherwise).
  const payMethod   = (isCatvPay ? config.catvPayMethod : config.bbPayMethod) || 'gateway';
  const upiEntries  = isCatvPay ? upiEntriesFor(config.catvUpiEntries, catvSource?.catv?.entityCode) : (config.upiEntries || []);
  const upiName     = isCatvPay ? config.catvUpiName : config.upiName;
  const activeUpiId = upiEntries[selUpi]?.upiId;
  const upiNote     = isCatvPay ? `CableTV - ${catvSource?.customerId || ''}` : `1-Month Renewal - ${bbSource?.customerId || ''}`;
  const upiLink     = buildUpiLink({ upiId: activeUpiId, upiName }, upiAmount, upiNote);

  // Screenshot auto-billing: OCR reads the amount + reference number and posts the
  // payment automatically.
  const showAutoUpload = upiAmount > 0;

  // Five Online PG scenarios on this page:
  //  - CATV regular / Broadband regular: pay off outstandingBalance (purpose:'balance')
  //  - Broadband dtd: renew the assigned plan for 1 month (purpose:'renewal')
  //  - CATV dtd: renew the assigned package for the chosen tier, discount computed
  //    server-side to match this page's own display exactly (purpose:'catv-dtd')
  //  - Broadband, no plan assigned + nothing owed, customer picked one: charge its price
  //    as a plain credit (purpose:'balance' + planId)
  // DTD CableTV after 8 PM with nothing still running: counts from tomorrow (enforced
  // server-side, utils/dtdCutoff.js) — the customer must accept that before paying.
  const needs8pmNotice = isCatvDtd && dtdStartsTomorrowForExpiry(catvSource?.catv?.expiryDate);
  // DTD, or regular but suspended on GTPL: the box must be on for the recharge to land
  // (utils/stbOnNotice.js). Asked first, then the after-8 PM notice.
  const needsStbNotice = isCatvPay && stbOnNoticeApplies(catvSource);
  const afterStbNotice = () => {
    setShowStbNotice(false);
    if (needs8pmNotice) setShow8pmNotice(true);
    else handlePayOnlinePg();
  };
  const onPayOnlinePgClick = () => (needsStbNotice ? setShowStbNotice(true) : afterStbNotice());

  const handlePayOnlinePg = async () => {
    setShow8pmNotice(false);
    const isBalance = isCatvRegular || isBbRegular || isBbUnassignedPicked;
    const purpose = isBalance ? 'balance' : isCatvDtd ? 'catv-dtd' : 'renewal';
    setPayingOnlinePg(true);
    try {
      await startOnlinePayment({
        service: isCatvPay ? 'catv' : 'broadband',
        purpose,
        planId: isBbUnassignedPicked ? pickedPlan._id : undefined,
        months: isBalance ? undefined : isCatvDtd ? dtdTier.months : BB_OFFER.months,
        packageId: isCatvDtd ? dtdPackage?._id : undefined,
        addonPlanIds: isBbDtd ? [...selectedAddonIds] : undefined,
        companyName: config.upiName || 'AngelNet',
        accentColor: isCatvPay ? '#7c3aed' : '#2563eb',
        description: isBbUnassignedPicked
          ? `Broadband — ${pickedPlan.displayName || pickedPlan.name}`
          : isBalance
            ? `${isCatvPay ? 'CableTV' : 'Broadband'} outstanding balance`
            : isCatvDtd
              ? `CableTV renewal — ${dtdTier.months} month(s)`
              : `Broadband renewal — ${BB_OFFER.months} month(s)`,
        onSuccess: () => {
          toast.success(isBalance ? 'Payment successful!' : 'Renewal successful!');
          paymentAPI.getAll({ limit: 20 }).then(pRes => setPayments(pRes.data.data || []));
          customerAPI.getMe().then(cRes => setCustomer(cRes.data.data));
          setSelectedAddonIds(new Set());
          setPayingOnlinePg(false);
        },
        onFailure: (message) => {
          if (message) toast.error(message);
          setPayingOnlinePg(false);
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not start payment');
      setPayingOnlinePg(false);
    }
  };

  const handleScreenshotChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('screenshot', file);
      fd.append('service', service);
      if (!isCatvPay && selectedAddonIds.size > 0) {
        fd.append('addonPlanIds', JSON.stringify([...selectedAddonIds]));
      }
      const { data } = await customerAPI.uploadPaymentScreenshot(fd);
      setUploadResult(data.data);
      if (data.data?.status === 'applied') {
        toast.success('Payment recorded — thank you!');
        paymentAPI.getAll({ limit: 20 }).then(pRes => setPayments(pRes.data.data || []));
        customerAPI.getMe().then(cRes => setCustomer(cRes.data.data));
        setSelectedAddonIds(new Set());
      } else {
        toast.error(data.data?.message || 'Could not process screenshot');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed — please try again');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 4 }}>Payments</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 20 }}>Pay your bill or view past payments</p>

      {/* Payment section — gated on having a service at all, not on the selected
          service having a price, so switching tabs never hides the selector itself. */}
      {!loading && customer && (hasCatv || hasBB) && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 8px rgba(0,0,0,.08)', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: '.95rem', marginBottom: 12 }}>Make a Payment</div>

          {/* No CableTV/Broadband selector for combo customers — business rule, 2026-07-27:
              they only see/pay Broadband here (`service` is forced above); a CableTV
              switch or payment goes through support as a manual request instead. */}

          {/* ── CATV / Regular: outstanding balance only — no packages, no offers ── */}
          {isCatvRegular && (
            <div>
              {catvOutstanding <= 0 ? (
                <div style={{ textAlign: 'center', padding: '18px 0' }}>
                  <Check size={28} color="#16a34a" style={{ marginBottom: 6 }} />
                  <p style={{ fontSize: '.88rem', color: 'var(--text-muted)', margin: 0 }}>No outstanding balance — you're all paid up.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, padding: '12px 14px', background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 10 }}>
                  <span style={{ fontSize: '.85rem', color: '#b91c1c', fontWeight: 600 }}>Outstanding Balance</span>
                  <span style={{ fontSize: '1.1rem', fontWeight: 800, color: '#b91c1c' }}>₹{fmt(catvOutstanding)}</span>
                </div>
              )}
            </div>
          )}

          {/* ── CATV / DTD: browse visible packages, pick a duration ── */}
          {isCatvDtd && (
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                Choose a package
              </p>
              {catvPackages.length > 0 ? (
                <div style={{ position: 'relative', marginBottom: 14 }}>
                  <select
                    value={dtdPackage?._id || ''}
                    onChange={(e) => setPickedPlan(catvPackages.find(p => p._id === e.target.value) || null)}
                    style={{
                      width: '100%', appearance: 'none', WebkitAppearance: 'none',
                      padding: '12px 38px 12px 14px', borderRadius: 10, border: '1.5px solid #e5e7eb',
                      background: '#fff', color: 'var(--text)', fontSize: '.85rem', fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {!dtdPackage && <option value="" disabled>Select a package…</option>}
                    {catvPackages.map(item => (
                      <option key={item._id} value={item._id}>
                        {item.displayName || item.name} — ₹{fmt(item.price)}/mo
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={16} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                </div>
              ) : (
                <p style={{ color: '#888', fontSize: '.82rem', marginBottom: 14 }}>No packages available right now — contact your ISP.</p>
              )}

              {dtdPackage && (
                <>
                  <p style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                    Choose duration
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8, marginBottom: 14 }}>
                    {dtdTiers.map(t => {
                      const eligible = !!(t.discountPayMonths && catvDiscountEligible);
                      const payMonths = eligible ? t.discountPayMonths : t.months;
                      return (
                        <button key={t.months} onClick={() => setDtdMonths(t.months)}
                          style={{
                            textAlign: 'left', padding: '10px 10px', borderRadius: 10, cursor: 'pointer',
                            border: `2px solid ${dtdMonths === t.months ? 'var(--primary)' : '#e5e7eb'}`,
                            background: dtdMonths === t.months ? '#eff6ff' : '#fff',
                          }}>
                          <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{t.label}</div>
                          <div style={{ fontSize: '.78rem', color: 'var(--primary)', fontWeight: 700, marginTop: 2 }}>₹{fmt(dtdPackage.price * payMonths)}</div>
                          {t.badge && (
                            eligible ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 4, fontSize: '.68rem', color: '#16a34a', fontWeight: 700 }}>
                                <Gift size={10} /> {t.badge}
                              </div>
                            ) : (
                              <div style={{ marginTop: 4, fontSize: '.66rem', color: '#9ca3af' }}>
                                Discount within {DISCOUNT_WINDOW_DAYS} days of expiry
                              </div>
                            )
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {catvSource?.catv?.expiryDate && (
                    <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                      Current validity: <strong>{fmtDate(catvSource.catv.expiryDate)}</strong>
                      {catvDiscountEligible
                        ? <span style={{ color: '#16a34a', fontWeight: 600 }}> · discount pricing active</span>
                        : <span style={{ color: '#9ca3af' }}> · discount unlocks {DISCOUNT_WINDOW_DAYS} days before expiry</span>}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Broadband: pick a plan if none assigned, otherwise pay the assigned plan ── */}
          {!isCatvPay && (
            <div style={{ marginBottom: 14 }}>
              {assignedBbPrice === 0 ? (
                <div>
                  <p style={{ color: '#888', fontSize: '.85rem', margin: '0 0 10px' }}>
                    No broadband plan is assigned to this connection yet — search and pick one below to pay for it:
                  </p>
                  {bbPlans.length === 0 ? (
                    <p style={{ color: '#888', fontSize: '.82rem' }}>No plans available right now — contact your ISP.</p>
                  ) : (
                    <SearchableSelect
                      options={bbPlans.filter(p => p.type !== 'addon').map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${fmt(p.price)}${p.speed ? ` · ${p.speed.download} Mbps` : ''}` }))}
                      value={pickedPlan?._id || ''}
                      onChange={(id) => setPickedPlan(bbPlans.find(p => p._id === id) || null)}
                      placeholder="Search broadband plans…"
                    />
                  )}
                </div>
              ) : null}
              {assignedBbPrice === 0 && pickedPlan && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, fontSize: '.8rem' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Paying for <strong style={{ color: 'var(--text)' }}>{pickedPlan.displayName || pickedPlan.name}</strong></span>
                  <button onClick={() => setPickedPlan(null)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600, fontSize: '.8rem' }}>Change</button>
                </div>
              )}
            </div>
          )}

          {/* ── Broadband add-ons — optional, folded into whatever's paid below ── */}
          {showAddons && (
            <div style={{ marginBottom: 14 }}>
              <p style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>
                Add-ons (optional)
              </p>
              <div style={{ display: 'grid', gap: 8 }}>
                {bbAddons.map(addon => {
                  const checked = selectedAddonIds.has(addon._id);
                  return (
                    <label key={addon._id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${checked ? 'var(--primary)' : '#e5e7eb'}`,
                      background: checked ? '#eff6ff' : '#fff',
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.85rem', fontWeight: 600 }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleAddon(addon._id)} />
                        {addon.displayName || addon.name}
                      </span>
                      <span style={{ fontSize: '.82rem', fontWeight: 700, color: 'var(--primary)' }}>₹{fmt(addon.price)}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Online PG — CATV/Broadband regular pay off outstandingBalance;
              CATV/Broadband dtd renew the assigned plan/package. ── */}
          {payMethod === 'gateway' && ((isCatvRegular && catvOutstanding > 0) || (isCatvDtd && dtdPackage) || (isBbDtd && assignedBbPrice > 0) || (isBbRegular && bbOutstanding > 0) || isBbUnassignedPicked) && (
            <button onClick={onPayOnlinePgClick} disabled={payingOnlinePg}
              style={{ width: '100%', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '.88rem', marginBottom: 10 }}>
              {payingOnlinePg ? 'Starting payment…' : `Pay Online — ₹${fmt(onlinePgAmount)}`}
            </button>
          )}

          {showStbNotice && <StbOnNotice onConfirm={afterStbNotice} onCancel={() => setShowStbNotice(false)} />}
          {show8pmNotice && <DtdAfter8pmNotice onConfirm={handlePayOnlinePg} onCancel={() => setShow8pmNotice(false)} />}

          {/* ── QR flow — shown instead of the gateway when Settings → Payments says QR ── */}
          {payMethod === 'qr' && upiAmount > 0 && (
            <>
              <button onClick={() => setShowQR(s => !s)}
                style={{ width: '100%', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '.88rem' }}>
                {showQR ? 'Hide QR' : `Show QR Code — ₹${fmt(upiAmount)}`}
              </button>

              {showQR && upiLink && (
                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  {/* Bank/UPI-handle selector */}
                  {upiEntries.length > 1 && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {upiEntries.map((e, i) => (
                        <button key={i} onClick={() => setSelUpi(i)}
                          style={{ padding: '6px 14px', borderRadius: 8, border: `2px solid ${selUpi === i ? 'var(--primary)' : '#d1d5db'}`,
                            background: selUpi === i ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '.8rem', fontWeight: 600 }}>
                          {e.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'inline-block', background: '#fff', padding: 16, borderRadius: 12, border: '2px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
                    <QRCode value={upiLink} size={200} />
                    <div style={{ marginTop: 8, fontSize: '.75rem', color: '#888' }}>
                      {upiName} · ₹{fmt(upiAmount)}
                    </div>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <a href={upiLink} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      background: '#16a34a', color: '#fff', textDecoration: 'none',
                      borderRadius: 8, padding: '10px 18px', fontWeight: 600, fontSize: '.85rem',
                    }}>
                      <CreditCard size={14} /> Open in UPI App
                    </a>
                  </div>

                  <p style={{ fontSize: '.78rem', color: '#888', marginTop: 8 }}>
                    Or scan the QR above with PhonePe, GPay, Paytm or any UPI app
                  </p>

                  {showAutoUpload && (
                    <div style={{ marginTop: 14 }}>
                      <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleScreenshotChange} />
                      <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontWeight: 600, cursor: 'pointer', fontSize: '.85rem' }}>
                        <Upload size={14} /> {uploading ? 'Reading screenshot…' : 'After paying, upload screenshot'}
                      </button>
                      <p style={{ fontSize: '.75rem', color: '#888', marginTop: 6 }}>
                        We'll read the amount and reference number automatically and update your account.
                      </p>
                      {uploadResult && (
                        <div style={{
                          marginTop: 10, padding: '10px 14px', borderRadius: 8, fontSize: '.82rem', display: 'inline-block', textAlign: 'left',
                          background: uploadResult.status === 'applied' ? '#f0fdf4' : '#fffbeb',
                          border: `1px solid ${uploadResult.status === 'applied' ? '#bbf7d0' : '#fde68a'}`,
                          color: uploadResult.status === 'applied' ? '#166534' : '#92400e',
                        }}>
                          {uploadResult.status === 'applied'
                            ? <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Check size={14} /> Payment recorded{uploadResult.receiptNumber ? ` — receipt ${uploadResult.receiptNumber}` : ''}</div>
                            : uploadResult.message}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {showQR && !upiLink && (
                <p style={{ color: '#888', fontSize: '.85rem', marginTop: 10, textAlign: 'center' }}>
                  UPI payment not available. Contact your admin.
                </p>
              )}
            </>
          )}

        </div>
      )}

      {/* Payment history */}
      <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>Payment History</h3>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : payments.length === 0 ? (
        <div className="card empty-state" style={{ padding: 48 }}>
          <CreditCard size={32} />
          <h3>No payments yet</h3>
          <p>Your payment history will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {payments.map(p => (
            <div key={p._id} className="card">
              <div style={{ padding: '14px 18px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span className="font-mono" style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{p.receiptNumber}</span>
                    <span className={`badge badge-${p.status === 'completed' ? 'success' : 'warning'}`}>
                      {p.status === 'completed' ? 'Paid' : 'Pending'}
                    </span>
                  </div>
                  <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{p.paymentFor?.replace(/_/g, ' ')}</div>
                  {p.planSnapshot?.name && (
                    <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Plan: {p.planSnapshot.name}</div>
                  )}
                  <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    {format(new Date(p.paymentDate), 'dd MMM yyyy HH:mm')}
                    {p.paymentMethod === 'company_paid' ? ' · ' + paidByLabel(companyName) :
                      (p.paymentMethod && p.paymentMethod !== 'adjustment' && ` · via ${p.paymentMethod.replace('_', ' ').toUpperCase()}`)}
                    {p.transactionId && <span style={{ marginLeft: 6, fontFamily: 'monospace' }}>· {p.transactionId}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--success)' }}>₹{fmt(p.totalAmount)}</div>
                  {p.gstAmount > 0 && (
                    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>incl. ₹{fmt(p.gstAmount)} GST</div>
                  )}
                  {p.status === 'completed' && (
                    <a href={getPublicReceiptUrl(p)} target="_blank" rel="noreferrer"
                      className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}>
                      <Printer size={12} /> Receipt
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 24, padding: 14, background: 'var(--bg)', borderRadius: 8, fontSize: '.82rem', color: 'var(--text-muted)', textAlign: 'center' }}>
        For billing queries, contact your admin or raise a support ticket.
      </div>
    </div>
  );
}
